import React, { useState, useEffect, useRef } from "react";
import { ChevronRight, Clock, ArrowLeft, CheckCircle, AlertTriangle, DollarSign, TrendingUp } from "lucide-react";
import SharedNavbar from "../../components/SharedNavbar";

const THUMBNAIL = "https://images.unsplash.com/photo-1681505526188-b05e68c77582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwaHlzaWNpYW4lMjBjb250cmFjdCUyMG5lZ290aWF0aW9uJTIwaW5jb21lJTIwZmluYW5jaWFsJTIwcGxhbm5pbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzczNTE0OTQ3fDA&ixlib=rb-4.1.0&q=85";

function useFadeIn(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, visible] = useFadeIn();
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

function BulletList({ items, color = "blue" }) {
  const iconColor = color === "red" ? "text-red-400" : color === "green" ? "text-green-500" : "text-blue-500";
  const Icon = color === "red" ? AlertTriangle : CheckCircle;
  return (
    <ul className="space-y-2 mb-4">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 text-slate-700 text-base">
          <Icon size={16} className={`${iconColor} flex-shrink-0 mt-0.5`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionH2({ children, color = "blue" }) {
  const border = color === "red" ? "border-red-400" : color === "green" ? "border-green-500" : "border-blue-500";
  return <h2 className={`text-3xl font-bold text-slate-900 mb-4 tracking-tight border-l-4 ${border} pl-4`}>{children}</h2>;
}

function SectionH3({ children }) {
  return <h3 className="text-xl font-semibold text-slate-800 mb-2 mt-5">{children}</h3>;
}

function ExampleBox({ lines }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 mb-4 space-y-1">
      {lines.map((line, i) => (
        <p key={i} className={`text-base ${line.bold ? "font-semibold text-slate-900" : "text-slate-600"}`}>{line.text}</p>
      ))}
    </div>
  );
}

function InlineCTA() {
  return (
    <div className="rounded-2xl p-7 my-10 text-center" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", boxShadow: "0 12px 40px rgba(29,78,216,0.18)" }}>
      <p className="text-white font-extrabold text-xl mb-2">Stop Calculating Manually.</p>
      <p className="text-slate-300 text-sm mb-5">ONE CLICK is all it takes — The Fastest RVU Tracker. <span className="text-blue-400 font-semibold">PERIOD.</span></p>
      <a href="/register" data-testid="article-inline-cta" className="inline-flex items-center gap-2 text-sm font-bold text-white rounded-full px-7 py-3 transition-all hover:-translate-y-px hover:shadow-xl" style={{ background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", boxShadow: "0 4px 16px rgba(37,99,235,0.40)" }}>
        Try Doc RVU Tracker Free <ChevronRight size={15} />
      </a>
    </div>
  );
}

const ARTICLES_META = [
  { slug: "/rvu-articles/how-to-track-rvus", title: "How to Track RVUs: Complete Guide for Providers", desc: "Learn what RVUs are, why tracking matters, common mistakes, and the most effective approach to stay on top of your productivity.", category: "RVU Fundamentals", readTime: "6 min", badge: "bg-blue-50 text-blue-700 border-blue-200", img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxkb2N0b3IlMjBwcm92aWRlciUyMG1lZGljYWwlMjB0YWJsZXQlMjBkYXRhJTIwdHJhY2tpbmclMjBjbGluaWNhbHxlbnwwfHx8fDE3NzMzNjQ5Mzh8MA&ixlib=rb-4.1.0&q=85" },
  { slug: "/rvu-articles/missed-billing-codes", title: "Most Commonly Missed Billing Codes", desc: "Discover the CPT codes most frequently missed in urgent care, primary care, and the ER — and how to avoid lost income.", category: "Billing & Coding", readTime: "10 min", badge: "bg-orange-50 text-orange-700 border-orange-200", img: "https://images.unsplash.com/photo-1710503913397-df9bfea7e50b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwYmlsbGluZyUyMGNvZGluZyUyMGhvc3BpdGFsJTIwY2hhcnQlMjBwYXBlcndvcmt8ZW58MHx8fHwxNzczNDM5ODM1fDA&ixlib=rb-4.1.0&q=85" },
  { slug: "/rvu-articles/rvus-and-paycheck", title: "How RVUs Translate Into Your Paycheck", desc: "Understand how wRVUs directly convert to compensation, what models exist, and how to take control of your earnings.", category: "RVU & Compensation", readTime: "8 min", badge: "bg-green-50 text-green-700 border-green-200", img: "https://images.unsplash.com/photo-1681505526188-b05e68c77582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxwaHlzaWNpYW4lMjBjb250cmFjdCUyMG5lZ290aWF0aW9uJTIwaW5jb21lJTIwZmluYW5jaWFsJTIwcGxhbm5pbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzczNTE0OTQ3fDA&ixlib=rb-4.1.0&q=85" },
];

function TableOfContents({ items }) {
  return (
    <nav className="rounded-2xl border border-slate-200 bg-slate-50 p-5 mb-10" aria-label="Table of contents">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">In This Article</p>
      <ol className="space-y-1.5 list-none p-0 m-0">
        {items.map((item, i) => (
          <li key={item.id}>
            <a href={`#${item.id}`} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors">
              <span className="text-slate-400 text-xs min-w-[1.25rem]">{i + 1}.</span>
              {item.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function RelatedArticles({ currentSlug }) {
  const related = ARTICLES_META.filter((a) => a.slug !== currentSlug);
  return (
    <section className="mb-10 mt-2" aria-label="Related articles">
      <h2 className="text-2xl font-bold text-slate-900 mb-5 tracking-tight">Continue Reading</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {related.map((article) => (
          <a key={article.slug} href={article.slug} className="group block rounded-2xl border border-slate-200 bg-white overflow-hidden hover:border-blue-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 no-underline">
            <div className="aspect-[16/9] overflow-hidden">
              <img src={article.img} alt={article.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${article.badge}`}>{article.category}</span>
                <span className="text-xs text-slate-400">{article.readTime} read</span>
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-2 group-hover:text-blue-600 transition-colors leading-snug">{article.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{article.desc}</p>
              <div className="mt-3 flex items-center gap-1 text-blue-600 text-sm font-semibold">Read Article <ChevronRight size={13} /></div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

export default function RVUsAndPaycheck() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "How RVUs Translate Into Your Paycheck: Physician Compensation Guide | Doc RVU Tracker";
    const managed = [];
    function setMeta(selector, attr, val, content) {
      let el = document.querySelector(selector);
      const existed = !!el;
      const prevContent = existed ? el.getAttribute("content") : null;
      if (!existed) { el = document.createElement("meta"); document.head.appendChild(el); }
      el.setAttribute(attr, val); el.setAttribute("content", content);
      managed.push({ el, existed, prevContent });
    }
    setMeta('meta[name="description"]', "name", "description", "Understand how work RVUs (wRVUs) directly translate into physician and provider income. Learn about compensation models, conversion rates, and how to take full control of your earnings.");
    setMeta('meta[name="keywords"]', "name", "keywords", "RVU compensation, how RVUs translate to income, physician RVU paycheck, wRVU salary calculation, doctor RVU pay, physician compensation model, RVU conversion rate");
    setMeta('meta[property="og:title"]', "property", "og:title", "How RVUs Translate Into Your Paycheck: Physician Compensation Guide");
    setMeta('meta[property="og:description"]', "property", "og:description", "Understand how work RVUs directly translate into physician income. Learn compensation models, conversion rates, and how to take control of your earnings.");
    setMeta('meta[property="og:type"]', "property", "og:type", "article");
    setMeta('meta[property="og:url"]', "property", "og:url", `${window.location.origin}/rvu-articles/rvus-and-paycheck`);
    setMeta('meta[property="og:image"]', "property", "og:image", "https://images.unsplash.com/photo-1681505526188-b05e68c77582?w=1200&q=80");
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", "Doc RVU Tracker");
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", "How RVUs Translate Into Your Paycheck: Physician Compensation Guide");
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", "Understand how wRVUs translate into physician income. Learn about compensation models, conversion rates, and how to maximize your earnings.");
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", "https://images.unsplash.com/photo-1681505526188-b05e68c77582?w=1200&q=80");
    let canonical = document.querySelector('link[rel="canonical"]');
    const canonicalExisted = !!canonical;
    const prevHref = canonicalExisted ? canonical.getAttribute("href") : null;
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `${window.location.origin}/rvu-articles/rvus-and-paycheck`);
    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Article", "headline": "How RVUs Translate Into Your Paycheck: Physician Compensation Guide", "description": "Understand how work RVUs directly translate into physician and provider income. Learn about compensation models, conversion rates, and how to take control of your earnings.", "author": { "@type": "Organization", "name": "Doc RVU Tracker", "url": window.location.origin }, "publisher": { "@type": "Organization", "name": "Doc RVU Tracker", "url": window.location.origin, "logo": { "@type": "ImageObject", "url": `${window.location.origin}/rvu-logo.png` } }, "datePublished": "2026-03-01", "dateModified": "2026-03-01", "image": "https://images.unsplash.com/photo-1681505526188-b05e68c77582?w=1200&q=80", "url": `${window.location.origin}/rvu-articles/rvus-and-paycheck`, "mainEntityOfPage": { "@type": "WebPage", "@id": `${window.location.origin}/rvu-articles/rvus-and-paycheck` } });
    document.head.appendChild(jsonLd);
    return () => {
      document.title = prevTitle;
      managed.forEach(({ el, existed, prevContent }) => { if (existed) el.setAttribute("content", prevContent); else el.parentNode && el.parentNode.removeChild(el); });
      if (canonicalExisted) canonical.setAttribute("href", prevHref); else canonical.parentNode && canonical.parentNode.removeChild(canonical);
      jsonLd.parentNode && jsonLd.parentNode.removeChild(jsonLd);
    };
  }, []);

  return (
    <div className="min-h-screen font-sans antialiased" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif", background: "#f8fafc" }}>
      <SharedNavbar activePage="articles" />

      {/* Article Header */}
      <section className="pt-28 pb-0 px-4" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <a href="/rvu-articles" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 mb-6 transition-colors" data-testid="back-to-articles">
            <ArrowLeft size={15} /> Back to Articles
          </a>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-semibold px-3 py-1 rounded-full border bg-green-50 text-green-700 border-green-200">RVU & Compensation</span>
            <span className="flex items-center gap-1 text-xs text-slate-400"><Clock size={11} /> 8 min read</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
            How RVUs Translate Into
            <span style={{ background: "linear-gradient(135deg, #2563eb 0%, #0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}> Your Paycheck</span>
          </h1>
        </div>
      </section>

      {/* Hero image */}
      <div className="px-4 pb-10" style={{ background: "linear-gradient(160deg, #f0f7ff 0%, #f8fafc 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-md" style={{ maxHeight: 380 }}>
            <img src={THUMBNAIL} alt="Physician reviewing RVU compensation and contract terms for income planning" loading="lazy" className="w-full object-cover" style={{ height: 380, objectPosition: "center top" }} />
          </div>
        </div>
      </div>

      {/* Article Body */}
      <article className="px-4 pb-16">
        <div className="max-w-3xl mx-auto">

          {/* Intro */}
          <FadeIn>
            <div className="mb-10">
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Many clinicians understand that Relative Value Units (RVUs) are used to measure productivity — but far fewer understand how RVUs directly translate into their actual income.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">This lack of clarity can lead to misunderstanding compensation structures, underestimating true earning potential, missing opportunities to optimize productivity, and feeling underpaid without objective data.</p>
              <p className="text-slate-700 leading-relaxed">
                Whether you work in urgent care, primary care, or emergency medicine, understanding how RVUs influence your paycheck is essential for both financial planning and career development.
              </p>
            </div>
          </FadeIn>

          {/* Table of Contents */}
          <FadeIn delay={30}>
            <TableOfContents items={[
              { id: "what-are-rvus", label: "What Are RVUs and Why They Matter?" },
              { id: "compensation-models", label: "Common Compensation Models" },
              { id: "why-providers-misunderstand", label: "Why Providers Misunderstand Their Earnings" },
              { id: "rvu-conversion-rate", label: "Understanding Your RVU Conversion Rate" },
              { id: "shift-based-medicine", label: "Shift-Based Medicine & Income Tracking" },
              { id: "how-tracking-helps", label: "How Tracking RVUs Improves Clarity" },
              { id: "track-with-one-click", label: "Track RVUs with Just 1 Click" },
            ]} />
          </FadeIn>

          {/* What Are RVUs */}
          <FadeIn delay={60}>
            <section id="what-are-rvus" className="mb-10 scroll-mt-24">
              <SectionH2>What Are RVUs and Why Do They Matter for Compensation?</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-3">Relative Value Units (RVUs) are a standardized metric used to quantify the value of medical services.</p>
              <p className="text-slate-700 leading-relaxed mb-3">RVUs consist of three components: work RVUs (wRVUs), which reflect provider effort, skill, and time; practice expense RVUs, which cover overhead costs; and malpractice RVUs, which account for liability costs.</p>
              <p className="text-slate-700 leading-relaxed mb-3">For most providers, work RVUs (wRVUs) are the component that directly influences compensation.</p>
              <p className="text-slate-700 leading-relaxed mb-4">Health systems and private practices use wRVUs to measure provider productivity, determine compensation bonuses, structure productivity-based contracts, and benchmark performance.</p>
              <p className="text-slate-600 text-sm italic p-4 bg-slate-50 border border-slate-200 rounded-xl">
                Even salaried or hourly providers are indirectly affected, since their productivity impacts practice revenue.
              </p>
            </section>
          </FadeIn>

          {/* Compensation Models */}
          <FadeIn delay={60}>
            <section id="compensation-models" className="mb-10 scroll-mt-24">
              <SectionH2>Common Compensation Models Based on RVUs</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-4">Understanding how RVUs convert into income requires familiarity with common compensation structures.</p>

              <SectionH3>Base Salary Plus RVU Bonus</SectionH3>
              <p className="text-slate-700 text-base mb-2">Many clinicians receive:</p>
              <BulletList items={["Fixed base salary", "Productivity bonus after exceeding a wRVU threshold"]} />
              <p className="text-slate-700 text-base font-medium mb-2">Example:</p>
              <ExampleBox lines={[
                { text: "Base salary: $140,000" },
                { text: "Bonus threshold: 4,500 wRVUs" },
                { text: "Conversion factor: $45 per wRVU" },
                { text: "If a provider produces 5,000 wRVUs:", bold: true },
                { text: "Bonus wRVUs: 500" },
                { text: "Bonus income: $22,500", bold: true },
              ]} />
              <p className="text-slate-600 text-sm p-3 bg-blue-50 border border-blue-100 rounded-xl mb-2">In this model, productivity tracking becomes critical.</p>

              <SectionH3>Pure Productivity Model</SectionH3>
              <p className="text-slate-700 text-base mb-2">Some contracts compensate providers entirely based on RVU output.</p>
              <p className="text-slate-700 text-base font-medium mb-2">Example:</p>
              <ExampleBox lines={[
                { text: "Conversion rate: $50 per wRVU" },
                { text: "Total annual production: 6,000 wRVUs" },
                { text: "Total income: $300,000", bold: true },
              ]} />
              <p className="text-slate-600 text-sm p-3 bg-blue-50 border border-blue-100 rounded-xl mb-2">In this model, RVU tracking directly equals income tracking.</p>

              <SectionH3>Hybrid Hourly + Productivity Models</SectionH3>
              <p className="text-slate-700 text-base mb-2">Common in urgent care and emergency medicine.</p>
              <p className="text-slate-700 text-base font-medium mb-2">Example:</p>
              <ExampleBox lines={[
                { text: "Hourly rate: $85/hour" },
                { text: "Additional productivity bonus based on wRVUs" },
              ]} />
              <p className="text-slate-700 text-base mb-4">Providers may not realize how much productivity contributes to annual income, performance evaluation, and contract renegotiation leverage.</p>
            </section>
          </FadeIn>

          {/* Inline CTA */}
          <FadeIn delay={60}><InlineCTA /></FadeIn>

          {/* Why Providers Misunderstand */}
          <FadeIn delay={60}>
            <section id="why-providers-misunderstand" className="mb-10 scroll-mt-24">
              <SectionH2 color="red">Why Providers Often Misunderstand Their True Earnings</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-3">Several factors contribute to this lack of clarity. RVU data is often delayed, compensation reports can be difficult to interpret, and shift intensity does not always correlate with actual RVU output. Billing accuracy also directly affects productivity metrics, and cognitive bias can lead to inaccurate perceptions of workload.</p>
              <p className="text-slate-700 leading-relaxed mb-4">As a result, providers may feel overworked but underpaid, productive but unrecognized, or simply busy without any financial clarity.</p>
              <p className="text-slate-600 text-sm italic p-4 bg-slate-50 border border-slate-200 rounded-xl">
                Without objective tracking, these perceptions are difficult to validate.
              </p>
            </section>
          </FadeIn>

          {/* RVU Conversion Rate */}
          <FadeIn delay={60}>
            <section id="rvu-conversion-rate" className="mb-10 scroll-mt-24">
              <SectionH2>The Importance of Understanding Your RVU Conversion Rate</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-3">Each organization sets its own RVU conversion factor, productivity thresholds, and bonus structures. Two providers with identical productivity can earn vastly different incomes depending on their contract terms, employer compensation models, specialty benchmarks, and geographic market factors.</p>
              <p className="text-slate-700 leading-relaxed mb-4">Understanding your specific conversion rate is therefore essential for financial planning, career decision-making, and contract negotiation.</p>
            </section>
          </FadeIn>

          {/* Shift-Based Medicine */}
          <FadeIn delay={60}>
            <section id="shift-based-medicine" className="mb-10 scroll-mt-24">
              <SectionH2>Why Shift-Based Medicine Makes Income Harder to Track</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-3">Providers in urgent care, emergency medicine, and hospitalist roles often experience wide variability in patient volume, case complexity, procedural opportunities, and RVU generation from shift to shift.</p>
              <p className="text-slate-700 leading-relaxed mb-4">Without tracking productivity per shift, it becomes difficult to estimate actual hourly earnings, identify high versus low productivity patterns, or forecast annual income with any confidence.</p>
            </section>
          </FadeIn>

          {/* How Tracking Helps */}
          <FadeIn delay={60}>
            <section id="how-tracking-helps" className="mb-10 scroll-mt-24">
              <SectionH2 color="green">How Tracking RVUs Improves Financial Clarity</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-4">When providers actively track their RVU production, they gain real-time insight into productivity, awareness of billing and documentation trends, a better understanding of compensation patterns, data to support performance discussions, and confidence in income accuracy.</p>
              <p className="text-slate-700 leading-relaxed mb-4">Tracking transforms compensation from:</p>
              <div className="flex flex-col sm:flex-row items-center gap-3 mb-4">
                <div className="flex-1 bg-red-50 border border-red-100 rounded-xl px-5 py-4 text-center">
                  <p className="text-red-700 font-semibold text-sm">Guesswork</p>
                </div>
                <div className="text-slate-400 font-bold text-lg">→</div>
                <div className="flex-1 bg-green-50 border border-green-100 rounded-xl px-5 py-4 text-center">
                  <p className="text-green-700 font-semibold text-sm">Objective data</p>
                </div>
              </div>
            </section>
          </FadeIn>

          {/* Track Patient Visits CTA Section */}
          <FadeIn delay={60}>
            <section id="track-with-one-click" className="mb-10 scroll-mt-24">
              <SectionH2>Track Patient Visits &amp; RVUs with Just 1 Click</SectionH2>
              <p className="text-slate-700 leading-relaxed mb-2">You're moving room to room.</p>
              <p className="text-slate-700 leading-relaxed mb-2">You're charting between patients.</p>
              <p className="text-slate-700 leading-relaxed mb-4">You don't have time to manually track every encounter.</p>
              <p className="text-slate-700 leading-relaxed mb-4">But you still deserve to know exactly what each shift earned, understand how your productivity translates into income, and make sure you're getting paid what you earned and deserve.</p>
              <p className="text-slate-700 leading-relaxed mb-3">
                <strong className="text-slate-900">Doc RVU Tracker</strong> was built by a practicing clinician to make productivity tracking quick and effortless.
              </p>
              <p className="text-slate-700 leading-relaxed mb-3">With just one click, you can log patient visits instantly, monitor RVU trends, estimate earnings per shift, and gain full clarity on your true productivity.</p>
              <p className="text-slate-600 text-sm italic mb-4">All while staying focused on what matters most — patient care.</p>
              <p className="text-slate-600 text-sm p-4 bg-blue-50 border border-blue-100 rounded-xl">
                Ready to take control of your earnings?{" "}
                <a href="/register" data-testid="article-cta-main" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors">
                  Start tracking your next shift today with Doc RVU Tracker →
                </a>
              </p>
            </section>
          </FadeIn>

          {/* Related Articles */}
          <FadeIn delay={60}>
            <RelatedArticles currentSlug="/rvu-articles/rvus-and-paycheck" />
          </FadeIn>

          {/* Final CTA */}
          <FadeIn delay={60}>
            <div className="rounded-2xl p-8 text-center mt-4" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #0369a1 100%)", boxShadow: "0 20px 60px rgba(29,78,216,0.25)" }}>
              <img src="/rvu-logo.png" alt="Doc RVU Tracker" style={{ width: 56, height: 56, borderRadius: "14px", objectFit: "cover", boxShadow: "0 4px 16px rgba(22,163,74,0.40)", margin: "0 auto 16px" }} />
              <h3 className="text-2xl font-extrabold text-white mb-2 tracking-tight">Start tracking your next shift today.</h3>
              <p className="text-blue-100 text-base mb-6">Track your RVUs with ONE CLICK. The Fastest RVU Tracker. PERIOD.</p>
              <a href="/register" data-testid="article-final-cta" className="inline-flex items-center gap-2 text-base font-bold text-white rounded-full px-8 py-3.5 transition-all hover:-translate-y-px hover:shadow-xl" style={{ background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.40)" }}>
                Get Started at DocRVUTracker.com <ChevronRight size={18} />
              </a>
            </div>
          </FadeIn>

        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-10 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5">
          <div>
            <div className="flex items-center gap-2.5 mb-1.5">
              <img src="/rvu-logo.png" alt="Doc RVU Tracker" style={{ width: 30, height: 30, borderRadius: "7px", objectFit: "cover" }} />
              <span className="text-white font-semibold text-sm">Doc RVU Tracker</span>
            </div>
            <p className="text-xs text-slate-500">The One-Click tracker built for providers by providers.</p>
            <p className="text-xs text-slate-600">© {new Date().getFullYear()} Doc RVU Tracker. All rights reserved.</p>
          </div>
          <div className="flex flex-col items-center md:items-end gap-1">
            <div className="flex gap-5 text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <a href="/rvu-articles" className="hover:text-white transition-colors">Articles</a>
              <a href="/free-rvu-calculator" className="hover:text-white transition-colors">Free Calculator</a>
              <a href="/login" className="hover:text-white transition-colors">Log In</a>
            </div>
            <p className="text-sm font-medium text-slate-300 mt-1">DocRVUTracker@gmail.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
